import { useEffect, useRef, useState } from "react";
import { PieChart } from "@mui/x-charts/PieChart";
import { OvalLoader } from "../../../components/common/Loader";
import { CHART_PALETTE } from "../../../constant/theme";
import "./ChapterWiseReportPieChart.css";

const pieSx = {
  "& .MuiPieArcLabel-root": {
    fill: "#fff",
    fontWeight: 600,
    fontSize: "13px",
  },
};

const ChapterWiseReportPieChart = ({
  loading,
  chartData,
  total,
  valueFormatter,
  totalLabel = "Total",
  totalValue,
  loaderColor = "#0c3c7c",
}) => {
  const leftRef = useRef(null);
  const [pieSize, setPieSize] = useState(400);

  const dataWithValues = (chartData || [])
    .filter((item) => item.value > 0)
    .map((item, index) => {
      const pct = total > 0 ? ((item.value / total) * 100).toFixed(1) : "0.0";
      return {
        ...item,
        color: CHART_PALETTE[index % CHART_PALETTE.length],
        pct,
        label: `${item.label} - ${pct}%`,
      };
    });

  useEffect(() => {
    if (!leftRef.current) return;

    const updateSize = () => {
      if (!leftRef.current) return;
      const { width, height } = leftRef.current.getBoundingClientRect();
      const size = Math.min(width, height) + 8;
      setPieSize(Math.max(320, Math.min(size, 580)));
    };

    updateSize();
    const observer = new ResizeObserver(updateSize);
    observer.observe(leftRef.current);
    return () => observer.disconnect();
  }, [loading, dataWithValues.length]);

  const outerRadius = Math.round((pieSize - 20) / 2);
  const innerRadius = Math.round(outerRadius * 0.35);

  return (
    <div className="chapter-report-pie-wrapper">
      {loading ? (
        <div className="chapter-report-pie-loader">
          <OvalLoader color={loaderColor} />
        </div>
      ) : dataWithValues.length > 0 ? (
        <>
          <div className="chapter-report-pie-total">
            <span className="chapter-report-pie-total-label">{totalLabel}</span>
            <span className="chapter-report-pie-total-value">{totalValue}</span>
          </div>

          <div className="chapter-report-pie-layout">
            <div ref={leftRef} className="chapter-report-pie-left">
              <PieChart
                colors={CHART_PALETTE}
                series={[
                  {
                    data: dataWithValues,
                    innerRadius,
                    outerRadius,
                    paddingAngle: 1,
                    cornerRadius: 3,
                    highlightScope: { faded: "global", highlighted: "item" },
                    faded: {
                      innerRadius,
                      additionalRadius: -30,
                      color: "gray",
                    },
                    valueFormatter,
                    arcLabel: (item) => {
                      const pct =
                        total > 0 ? ((item.value / total) * 100).toFixed(0) : 0;
                      return pct >= 5 ? `${pct}%` : "";
                    },
                    arcLabelMinAngle: 20,
                  },
                ]}
                width={pieSize}
                height={pieSize}
                margin={{ top: 12, right: 12, bottom: 12, left: 12 }}
                sx={pieSx}
                slotProps={{ legend: { hidden: true } }}
              />
            </div>

            <div className="chapter-report-pie-legend">
              {dataWithValues.map((item) => (
                <div key={item.id} className="chapter-report-pie-legend-item">
                  <span
                    className="chapter-report-pie-legend-dot"
                    style={{ backgroundColor: item.color }}
                  />
                  <span className="chapter-report-pie-legend-text">
                    {item.label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <p className="chapter-report-pie-note">
            Showing all {dataWithValues.length} chapter
            {dataWithValues.length !== 1 ? "s" : ""} with data
          </p>
        </>
      ) : (
        <div className="chapter-report-pie-empty">No data available</div>
      )}
    </div>
  );
};

export default ChapterWiseReportPieChart;
